import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  TextField,
  Typography,
  FormControlLabel,
  Checkbox,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress
} from '@mui/material';
import ZipCodeFormatCustom from '../../../components/formatters/ZipCodeFormatCustom';
import PhoneNumberFormatCustom from '../../../components/formatters/PhoneNumberFormatCustom';
import EmailFormatCustom from '../../../components/formatters/EmailFormatCustom';
import { Constants } from '../../../Constants';
import { ProvidersService } from '../../../services/ProvidersService';
import { SupportService } from '../../../services/SupportService';
import ClaimBusinessDialog from '../../../components/ClaimBusinessDialog';

const BusinessInfoStep = ({ keycloak, provider, onNext, duplicateConflict, onClearDuplicateConflict }) => {
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    city: '',
    state: '',
    postalCode: '',
    email: '',
    phoneNumber: '',
    businessDescription: '',
    shortDescription: '',
    businessSummary: ''
  });

  const [fieldErrors, setFieldErrors] = useState({});
  const [agreedToBusinessTos, setAgreedToBusinessTos] = useState(false);
  const [duplicateDialog, setDuplicateDialog] = useState({ open: false, type: null, data: null });
  const [claimDialogOpen, setClaimDialogOpen] = useState(false);
  const [supportTicketSubmitting, setSupportTicketSubmitting] = useState(false);
  const [supportTicketMessage, setSupportTicketMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // Pre-fill from provider if resuming
  useEffect(() => {
    if (provider) {
      setFormData({
        name: provider.name || '',
        address: provider.address || '',
        city: provider.city || '',
        state: provider.state || '',
        postalCode: provider.postalCode || '',
        email: provider.email || '',
        phoneNumber: provider.phoneNumber || '',
        businessDescription: provider.businessDescription || '',
        shortDescription: provider.shortDescription || '',
        businessSummary: provider.businessSummary || ''
      });
      // Initialize ToS agreement state
      const hasCurrentTos = provider.businessTosVersion === Constants.BUSINESS_TOS_VERSION;
      setAgreedToBusinessTos(hasCurrentTos);
    }
  }, [provider]);

  // Handle backstop duplicate conflict from save error
  useEffect(() => {
    if (duplicateConflict) {
      const handleBackstop = async () => {
        // Re-check with current form values to get full duplicate info
        const checkData = {
          name: formData.name,
          address: formData.address,
          city: formData.city,
          state: formData.state,
          postalCode: formData.postalCode,
          email: formData.email,
          phoneNumber: formData.phoneNumber,
        };

        const reCheckResult = await ProvidersService.checkDuplicateProvider(checkData);

        // If re-check succeeds and returns helpful data, use it
        if (reCheckResult.status === 200) {
          const dupData = reCheckResult.data;
          if (dupData.status === 'claimed' || (dupData.status === 'unclaimed' && dupData.verification !== 'none')) {
            // Use the full data from re-check
            setDuplicateDialog({
              open: true,
              type: dupData.status === 'claimed' ? 'claimed' : 'unclaimed-claim',
              data: dupData
            });
            if (dupData.status === 'unclaimed') {
              setClaimDialogOpen(true);
            }
            onClearDuplicateConflict();
            return;
          }
        }

        // Fallback: use the backstop data from the 409 response
        if (duplicateConflict.duplicate === 'claimed') {
          setDuplicateDialog({
            open: true,
            type: 'claimed',
            data: {
              name: duplicateConflict.providerId,
              providerId: duplicateConflict.providerId
            }
          });
        } else if (duplicateConflict.duplicate === 'unclaimed') {
          setDuplicateDialog({
            open: true,
            type: 'unclaimed-claim',
            data: {
              providerId: duplicateConflict.providerId,
              name: duplicateConflict.providerId
            }
          });
          setClaimDialogOpen(true);
        }
        onClearDuplicateConflict();
      };

      handleBackstop();
    }
  }, [duplicateConflict, formData, onClearDuplicateConflict]);

  const handleChange = (evt) => {
    const { name, value, required } = evt.target;

    // Clear error for this field
    const newErrors = { ...fieldErrors, [name]: '' };

    // Validate required fields
    if (required && value.length < 1) {
      newErrors[name] = 'This field is required.';
    }

    setFieldErrors(newErrors);
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleInvalid = (evt) => {
    evt.preventDefault();
    const { name, validationMessage } = evt.target;
    setFieldErrors({
      ...fieldErrors,
      [name]: validationMessage
    });
  };

  // Extract provider build logic for reuse
  const buildProvider = () => {
    const baseProvider = {
      ...(provider || { id: keycloak.tokenParsed.sub }),
      name: formData.name,
      address: formData.address,
      city: formData.city,
      state: formData.state,
      postalCode: formData.postalCode,
      email: formData.email,
      phoneNumber: formData.phoneNumber,
      categoryType: provider?.categoryType || "NONE",
      businessDescription: formData.businessDescription,
      shortDescription: formData.shortDescription,
      businessSummary: formData.businessSummary,
      servicesProvided: (provider?.servicesProvided) || []
    };

    // Set ToS fields if newly agreed (not already accepted this version)
    if (agreedToBusinessTos && provider?.businessTosVersion !== Constants.BUSINESS_TOS_VERSION) {
      baseProvider.businessTosVersion = Constants.BUSINESS_TOS_VERSION;
      baseProvider.businessTosAcceptedAt = new Date().toISOString();
    }

    return baseProvider;
  };

  const isNewRegistration = !provider?.id || provider?.id === keycloak?.tokenParsed?.sub;

  const handleSubmitSupportTicket = async () => {
    setSupportTicketSubmitting(true);
    try {
      const ticketData = {
        role: 'Providers',
        issue: 'General',
        email: formData.email,
        title: `Claimed business registration attempt: ${formData.name}`,
        additionalInfo: `Provider ID: ${duplicateDialog.data?.providerId}\nFriendly URL: ${duplicateDialog.data?.friendlyUrl}\nAddress: ${formData.address}, ${formData.city}, ${formData.state} ${formData.postalCode}\nPhone: ${formData.phoneNumber}`,
        priority: 'Normal',
      };

      const response = await SupportService.submitSupportTicket(ticketData);
      if (response?.status === 201) {
        setSupportTicketMessage(`Thanks — our team will contact you at ${formData.email}.`);
      } else {
        setSupportTicketMessage('Failed to submit support request. Please try again.');
      }
    } catch (err) {
      setSupportTicketMessage('An error occurred. Please try again.');
      console.error('Error submitting support ticket:', err);
    } finally {
      setSupportTicketSubmitting(false);
    }
  };

  const handleClaimDialogClose = () => {
    setClaimDialogOpen(false);
    setDuplicateDialog({ open: false, type: null, data: null });
  };

  const handleUnclaimedNoneFlow = async () => {
    setIsSubmitting(true);
    try {
      // Store keycloak reference in ProvidersService if not already set
      if (!ProvidersService.keycloak && keycloak) {
        ProvidersService.keycloak = keycloak;
      }

      const result = await ProvidersService.claimProvider(duplicateDialog.data?.invitation);
      if (result?.status >= 200 && result?.status < 300) {
        // The invitation was claimed successfully. Load the claimed provider.
        try {
          const claimedProvider = await ProvidersService.getMyProvider(keycloak);
          setDuplicateDialog({ open: false, type: null, data: null });
          // Show success message and then advance
          setSuccessMessage('We found your existing listing and linked it to your account.');
          // Advance after a short delay to let the user see the message
          setTimeout(() => {
            onNext(claimedProvider);
          }, 1500);
        } catch (err) {
          setDuplicateDialog({
            open: true,
            type: 'error',
            data: { message: 'Could not load the claimed provider. Please refresh the page and try again.' }
          });
          console.error('Error loading claimed provider:', err);
        }
      } else {
        setDuplicateDialog({
          open: true,
          type: 'error',
          data: { message: 'Failed to claim the provider. Please try again.' }
        });
      }
    } catch (err) {
      setDuplicateDialog({
        open: true,
        type: 'error',
        data: { message: 'An error occurred. Please try again.' }
      });
      console.error('Error claiming provider:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFormSubmit = async (evt) => {
    evt.preventDefault();
    setIsSubmitting(true);

    try {
      // Only check for duplicates on fresh registrations (no existing provider loaded)
      if (isNewRegistration) {
        const checkData = {
          name: formData.name,
          address: formData.address,
          city: formData.city,
          state: formData.state,
          postalCode: formData.postalCode,
          email: formData.email,
          phoneNumber: formData.phoneNumber,
        };

        const duplicateCheckResult = await ProvidersService.checkDuplicateProvider(checkData);

        // If check call itself fails, proceed as normal
        if (duplicateCheckResult.status !== 200) {
          const newProvider = buildProvider();
          onNext(newProvider);
          setIsSubmitting(false);
          return;
        }

        const dupData = duplicateCheckResult.data;

        // Handle different duplicate scenarios
        if (dupData.status === 'none') {
          // No duplicate, proceed normally
          const newProvider = buildProvider();
          onNext(newProvider);
        } else if (dupData.status === 'unclaimed' && dupData.verification === 'mismatch') {
          // Mismatch case - proceed as today
          const newProvider = buildProvider();
          onNext(newProvider);
        } else if (dupData.status === 'claimed') {
          // Claimed case - show claimed dialog
          setDuplicateDialog({
            open: true,
            type: 'claimed',
            data: dupData
          });
        } else if (dupData.status === 'unclaimed') {
          if (dupData.verification === 'email' || dupData.verification === 'phone') {
            // Show claim dialog pre-filled with form data
            setDuplicateDialog({
              open: true,
              type: 'unclaimed-claim',
              data: dupData
            });
            setClaimDialogOpen(true);
          } else if (dupData.verification === 'none') {
            // Auto-claim case
            setDuplicateDialog({
              open: true,
              type: 'unclaimed-none',
              data: dupData
            });
          }
        }
      } else {
        // Existing provider or resumed registration - proceed normally
        const newProvider = buildProvider();
        onNext(newProvider);
      }
    } catch (err) {
      console.error('Error in form submission:', err);
      const newProvider = buildProvider();
      onNext(newProvider);
    } finally {
      setIsSubmitting(false);
    }
  };

  const hasGeneratedDescription = Boolean(
    (provider?.businessSummary && provider.businessSummary.trim()) ||
    (provider?.shortDescription && provider.shortDescription.trim())
  );

  return (
    <Box
      component="form"
      onSubmit={handleFormSubmit}
      onInvalid={handleInvalid}
      display="grid"
      gap="10px"
      sx={{ maxWidth: '600px', margin: '0 auto' }}
    >
      <Typography variant="h4" sx={{ marginBottom: '20px' }}>
        Business Information
      </Typography>

      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}

      {/* Business Name */}
      <TextField
        margin="normal"
        label="Business Name"
        name="name"
        onChange={handleChange}
        required
        value={formData.name}
        fullWidth
        error={!!fieldErrors.name}
        helperText={fieldErrors.name}
      />

      {/* Address */}
      <TextField
        margin="normal"
        label="Address"
        name="address"
        onChange={handleChange}
        required
        value={formData.address}
        fullWidth
        error={!!fieldErrors.address}
        helperText={fieldErrors.address}
      />

      {/* City */}
      <TextField
        margin="normal"
        label="City"
        name="city"
        onChange={handleChange}
        required
        value={formData.city}
        fullWidth
        error={!!fieldErrors.city}
        helperText={fieldErrors.city}
      />

      {/* State */}
      <TextField
        margin="normal"
        label="State"
        name="state"
        onChange={handleChange}
        required
        value={formData.state}
        fullWidth
        error={!!fieldErrors.state}
        helperText={fieldErrors.state}
      />

      {/* Zip Code */}
      <TextField
        margin="normal"
        label="Zip Code"
        name="postalCode"
        InputProps={{ inputComponent: ZipCodeFormatCustom }}
        onChange={handleChange}
        required
        value={formData.postalCode}
        fullWidth
        error={!!fieldErrors.postalCode}
        helperText={fieldErrors.postalCode}
      />

      {/* Email */}
      <TextField
        margin="normal"
        label="Email"
        name="email"
        InputProps={{ inputComponent: EmailFormatCustom }}
        onChange={handleChange}
        required
        value={formData.email}
        fullWidth
        error={!!fieldErrors.email}
        helperText={fieldErrors.email}
      />

      {/* Phone Number */}
      <TextField
        margin="normal"
        label="Phone Number"
        name="phoneNumber"
        InputProps={{ inputComponent: PhoneNumberFormatCustom }}
        onChange={handleChange}
        required
        value={formData.phoneNumber}
        fullWidth
        error={!!fieldErrors.phoneNumber}
        helperText={fieldErrors.phoneNumber}
      />

      {/* Business Description */}
      {hasGeneratedDescription ? (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ marginTop: '8px' }}>
            We generated these descriptions from your public information. Review and edit them if you'd like.
          </Typography>
          <TextField
            margin="normal"
            label="Short Description"
            name="shortDescription"
            onChange={handleChange}
            value={formData.shortDescription}
            fullWidth
            multiline
            minRows={2}
          />
          <TextField
            margin="normal"
            label="Business Summary"
            name="businessSummary"
            onChange={handleChange}
            value={formData.businessSummary}
            fullWidth
            multiline
            minRows={4}
          />
        </>
      ) : (
        <TextField
          margin="normal"
          label="Short Business Description"
          name="businessDescription"
          onChange={handleChange}
          value={formData.businessDescription}
          fullWidth
          multiline
          minRows={4}
          helperText="A few sentences about your business. We'll use this to help generate your profile content."
        />
      )}

      {/* Business Terms Checkbox */}
      <FormControlLabel
        sx={{ marginTop: '10px' }}
        control={
          <Checkbox
            checked={agreedToBusinessTos}
            onChange={(e) => setAgreedToBusinessTos(e.target.checked)}
            required
          />
        }
        label={
          <span>
            I agree to the{' '}
            <Link
              href="/business-terms"
              target="_blank"
              rel="noopener"
              sx={{ cursor: 'pointer' }}
            >
              PairCare Business Terms of Service
            </Link>
          </span>
        }
      />

      {/* Action Buttons */}
      <Box sx={{ marginTop: '20px', display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
        <Button
          type="submit"
          variant="contained"
          size="large"
          disabled={!agreedToBusinessTos || isSubmitting}
          startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
        >
          Save & Continue
        </Button>
      </Box>

      {/* Claimed Business Dialog */}
      <Dialog open={duplicateDialog.open && duplicateDialog.type === 'claimed'} maxWidth="sm" fullWidth>
        <DialogTitle>This Business Is Already On PairCare</DialogTitle>
        <DialogContent>
          <Typography sx={{ mt: 2 }}>
            {duplicateDialog.data?.name} is already registered and managed by its owner. If this is your business, our team can help sort it out.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDuplicateDialog({ open: false, type: null, data: null })}
            disabled={supportTicketSubmitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmitSupportTicket}
            variant="contained"
            disabled={supportTicketSubmitting}
            startIcon={supportTicketSubmitting ? <CircularProgress size={20} /> : null}
          >
            Contact Support
          </Button>
        </DialogActions>
      </Dialog>

      {/* Support Ticket Submitted Dialog */}
      <Dialog open={!!supportTicketMessage} maxWidth="sm" fullWidth>
        <DialogTitle>Support Request Submitted</DialogTitle>
        <DialogContent>
          <Typography sx={{ mt: 2 }}>{supportTicketMessage}</Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setSupportTicketMessage('');
              setDuplicateDialog({ open: false, type: null, data: null });
            }}
            variant="contained"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Unclaimed - None Flow Dialog */}
      <Dialog open={duplicateDialog.open && duplicateDialog.type === 'unclaimed-none'} maxWidth="sm" fullWidth>
        <DialogTitle>Found Your Existing Listing</DialogTitle>
        <DialogContent>
          <Typography sx={{ mt: 2 }}>
            We found your existing listing on PairCare. Let's verify it's yours and link it to your account.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDuplicateDialog({ open: false, type: null, data: null })}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUnclaimedNoneFlow}
            variant="contained"
            disabled={isSubmitting}
            startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
          >
            Link Listing
          </Button>
        </DialogActions>
      </Dialog>

      {/* Unclaimed - Claim Dialog for email/phone verification */}
      <ClaimBusinessDialog
        open={claimDialogOpen && duplicateDialog.type === 'unclaimed-claim'}
        onClose={handleClaimDialogClose}
        provider={duplicateDialog.data ? { id: duplicateDialog.data.providerId, name: duplicateDialog.data.name } : null}
        initialBusinessName={formData.name}
        initialEmail={formData.email}
        initialPhone={formData.phoneNumber}
      />

      {/* Error Dialog */}
      <Dialog open={duplicateDialog.open && duplicateDialog.type === 'error'} maxWidth="sm" fullWidth>
        <DialogTitle>Error</DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mt: 2 }}>
            {duplicateDialog.data?.message || 'An error occurred. Please try again.'}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDuplicateDialog({ open: false, type: null, data: null })}
            variant="contained"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

BusinessInfoStep.propTypes = {
  keycloak: PropTypes.object.isRequired,
  provider: PropTypes.object,
  onNext: PropTypes.func.isRequired
};

export default BusinessInfoStep;
